import { useState, useMemo, useEffect } from 'react';
import {
  RefreshCw,
  Wifi,
  WifiOff,
  MapPin,
  Clock,
  Loader2,
  Trash2,
  Search,
  ArrowLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Activity,
  Package,
  BarChart3,
  Settings2,
  AlertTriangle,
  TrendingUp,
  Zap,
  StopCircle,
  Ban,
  Plus,
  Tag,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { useAdminTenants, useAdminTenantDetail } from '@/hooks/use-admin';
import {
  useOctopusIntegration,
  useSaveOctopusIntegration,
  useTestOctopusConnection,
  useTriggerOctopusSync,
  useTriggerFullOctopusSync,
  useCancelOctopusSync,
  useForceCancelOctopusSync,
  useDiscoverOctopusLocations,
  useOctopusLocationMappings,
  useSaveOctopusLocationMappings,
  useDeleteOctopusLocationMapping,
  useOctopusSyncStats,
  useOctopusSyncConfig,
  useDiscoverOctopusVendors,
  useOctopusVendorSelections,
  useSaveOctopusVendorSelections,
  useOctopusUnitMappings,
  useSaveOctopusUnitMappings,
  useDeleteOctopusUnitMapping,
} from '@/hooks/use-octopus-integration';
import type { SyncStats, SyncRun, OctopusUnitMapping } from '@/hooks/use-octopus-integration';

// ── Sync Status Badge (presentational) ──
function SyncStatusBadge({ syncStatus }: { syncStatus: 'active' | 'disabled' | 'not_configured' | 'loading' }) {
  if (syncStatus === 'loading') {
    return (
      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-gray-50 text-gray-400">...</span>
    );
  }
  if (syncStatus === 'not_configured') {
    return (
      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
        Not Configured
      </span>
    );
  }
  if (syncStatus === 'active') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
        <Zap className="h-2.5 w-2.5" /> Sync Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
      <XCircle className="h-2.5 w-2.5" /> Sync Disabled
    </span>
  );
}

// ── Hook to resolve sync status for a single tenant ──
function useTenantSyncStatus(tenantId: string): 'active' | 'disabled' | 'not_configured' | 'loading' {
  const { data, isLoading } = useOctopusSyncConfig(tenantId);
  if (isLoading) return 'loading';
  const config = data?.data;
  if (!config) return 'not_configured';
  return config.isActive ? 'active' : 'disabled';
}

// ── Sync-filtered table body ──
function SyncFilteredBody({
  tenants,
  syncStatusFilter,
}: {
  tenants: { id: string; name: string; isActive: boolean }[];
  syncStatusFilter: 'all' | 'active' | 'disabled' | 'not_configured';
}) {
  return (
    <tbody>
      {tenants.length === 0 ? (
        <tr>
          <td colSpan={4} className="px-4 py-12 text-center text-sm text-muted-foreground">
            No businesses match your filters.
          </td>
        </tr>
      ) : (
        tenants.map((tenant) => (
          <FilterableTenantRow key={tenant.id} tenant={tenant} syncStatusFilter={syncStatusFilter} />
        ))
      )}
    </tbody>
  );
}

function FilterableTenantRow({
  tenant,
  syncStatusFilter,
}: {
  tenant: { id: string; name: string; isActive: boolean };
  syncStatusFilter: 'all' | 'active' | 'disabled' | 'not_configured';
}) {
  const syncStatus = useTenantSyncStatus(tenant.id);

  // Hide row if sync status filter is set and doesn't match
  // Don't hide while still loading
  if (syncStatusFilter !== 'all' && syncStatus !== 'loading' && syncStatus !== syncStatusFilter) {
    return null;
  }

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3 text-sm font-medium text-foreground">{tenant.name}</td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            tenant.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {tenant.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3">
        <SyncStatusBadge syncStatus={syncStatus} />
      </td>
      <td className="px-4 py-3">
        <a
          href={`#/admin/sync/${tenant.id}`}
          className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
        >
          Configure Sync
          <ChevronRight className="h-3 w-3" />
        </a>
      </td>
    </tr>
  );
}

// ── Tenant Picker ──
function TenantPicker() {
  const { data, isLoading } = useAdminTenants({ perPage: 100 });
  const tenants: any[] = data?.data ?? [];

  const [searchQuery, setSearchQuery] = useState('');
  const [tenantStatusFilter, setTenantStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [syncStatusFilter, setSyncStatusFilter] = useState<'all' | 'active' | 'disabled' | 'not_configured'>('all');

  // Client-side filtering by name and tenant status (sync status is filtered in render)
  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      // Name search
      if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      // Tenant status filter
      if (tenantStatusFilter === 'active' && !t.isActive) return false;
      if (tenantStatusFilter === 'inactive' && t.isActive) return false;
      return true;
    });
  }, [tenants, searchQuery, tenantStatusFilter]);

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <PageHeader title="X-Sync" description="Manage Octopus POS product sync per business" />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search businesses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={tenantStatusFilter}
          onChange={(e) => setTenantStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Tenants</option>
          <option value="active">Active Tenants</option>
          <option value="inactive">Inactive Tenants</option>
        </select>
        <select
          value={syncStatusFilter}
          onChange={(e) => setSyncStatusFilter(e.target.value as 'all' | 'active' | 'disabled' | 'not_configured')}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Sync Status</option>
          <option value="active">Sync Active</option>
          <option value="disabled">Sync Disabled</option>
          <option value="not_configured">Not Configured</option>
        </select>
        {(searchQuery || tenantStatusFilter !== 'all' || syncStatusFilter !== 'all') && (
          <button
            onClick={() => {
              setSearchQuery('');
              setTenantStatusFilter('all');
              setSyncStatusFilter('all');
            }}
            className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-muted"
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-gray-50">
            <tr className="border-b border-border">
              <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                Business Name
              </th>
              <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                Tenant Status
              </th>
              <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                Sync Status
              </th>
              <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">Actions</th>
            </tr>
          </thead>
          <SyncFilteredBody tenants={filteredTenants} syncStatusFilter={syncStatusFilter} />
        </table>
      </div>
    </div>
  );
}

// ── Main Export ──
export function AdminSyncPage({ tenantId }: { tenantId?: string }) {
  if (!tenantId) return <TenantPicker />;
  return <TenantSyncSettings tenantId={tenantId} />;
}

// ── Tab navigation ──
type TabId = 'monitoring' | 'setup' | 'history';

// ── Tenant Sync Settings ──
function TenantSyncSettings({ tenantId }: { tenantId: string }) {
  const { data: tenantRes } = useAdminTenantDetail(tenantId);
  const { data: statsRes } = useOctopusSyncStats(tenantId);
  const tenantName = tenantRes?.data?.name || 'Loading...';
  const stats: SyncStats | null = statsRes?.data ?? null;
  const [activeTab, setActiveTab] = useState<TabId>('monitoring');

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'monitoring', label: 'Monitoring', icon: BarChart3 },
    { id: 'setup', label: 'Setup', icon: Settings2 },
    { id: 'history', label: 'Sync History', icon: Clock },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <a
          href="#/admin/sync"
          className="inline-flex items-center justify-center rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </a>
        <div className="flex-1">
          <PageHeader title={`Sync — ${tenantName}`} description="Configure and monitor Octopus POS product sync" />
        </div>
        {stats?.integration && (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
              stats.integration.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {stats.integration.isActive ? (
              <>
                <Zap className="h-3 w-3" /> Sync Active
              </>
            ) : (
              <>
                <XCircle className="h-3 w-3" /> Sync Disabled
              </>
            )}
          </span>
        )}
      </div>

      {/* Tab navigation */}
      <div className="flex border-b border-border">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'monitoring' && <MonitoringTab tenantId={tenantId} stats={stats} />}
      {activeTab === 'setup' && <SetupTab tenantId={tenantId} />}
      {activeTab === 'history' && <HistoryTab tenantId={tenantId} stats={stats} />}
    </div>
  );
}

// ── Confirmation Dialog ──
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  isPending,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl border border-border bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              confirmVariant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Stat Card ──
function StatCard({
  label,
  value,
  icon: Icon,
  color = 'text-foreground',
  subtext,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
  subtext?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          {subtext && <p className="text-[10px] text-muted-foreground">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

// ── Monitoring Tab ──
function getLastSyncAgeMinutes(completedAt: string | null | undefined): number | null {
  if (!completedAt) return null;
  return Math.round((Date.now() - new Date(completedAt).getTime()) / 60000);
}

function MonitoringTab({ tenantId, stats }: { tenantId: string; stats: SyncStats | null }) {
  const triggerSync = useTriggerOctopusSync(tenantId);
  const triggerFullSync = useTriggerFullOctopusSync(tenantId);
  const cancelSync = useCancelOctopusSync(tenantId);
  const forceCancel = useForceCancelOctopusSync(tenantId);
  const lastSyncAge = getLastSyncAgeMinutes(stats?.lastSync?.completedAt);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [showFullSyncConfirm, setShowFullSyncConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showForceCancel, setShowForceCancel] = useState(false);

  const isSyncRunning = stats?.recentRuns?.some((r) => r.status === 'running') ?? false;

  function handleSyncNowClick() {
    if (isSyncRunning) {
      setShowSyncConfirm(true);
    } else {
      triggerSync.mutate();
    }
  }

  function handleConfirmSync() {
    setShowSyncConfirm(false);
    triggerSync.mutate();
  }

  function handleStopClick() {
    setShowStopConfirm(true);
  }

  function handleConfirmStop() {
    setShowStopConfirm(false);
    cancelSync.mutate();
  }

  function handleForceCancel() {
    setShowForceCancel(false);
    forceCancel.mutate();
  }

  if (!stats) {
    return <div className="flex h-32 items-center justify-center text-muted-foreground">Loading stats...</div>;
  }

  if (!stats.integration) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center">
        <Settings2 className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <h3 className="text-lg font-semibold">Octopus Integration Not Configured</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Go to the <strong>Setup</strong> tab to configure the Octopus POS connection for this business.
        </p>
      </div>
    );
  }

  const successRate24h = stats.last24h.successRate;

  return (
    <div className="space-y-6">
      {/* Confirmation dialogs */}
      <ConfirmDialog
        open={showSyncConfirm}
        title="Sync Already Running"
        message="A sync is currently in progress. Starting a new sync will cancel the running one. Are you sure you want to proceed?"
        confirmLabel="Start New Sync"
        onConfirm={handleConfirmSync}
        onCancel={() => setShowSyncConfirm(false)}
        isPending={triggerSync.isPending}
      />
      <ConfirmDialog
        open={showStopConfirm}
        title="Stop Running Sync"
        message="Are you sure you want to stop the currently running sync? Products already processed will be kept."
        confirmLabel="Stop Sync"
        confirmVariant="danger"
        onConfirm={handleConfirmStop}
        onCancel={() => setShowStopConfirm(false)}
        isPending={cancelSync.isPending}
      />
      <ConfirmDialog
        open={showForceCancel}
        title="Force Cancel Stuck Sync"
        message="This will forcefully terminate the sync process and clear all internal state. Use this only when a normal cancel isn't working."
        confirmLabel="Force Cancel"
        confirmVariant="danger"
        onConfirm={handleForceCancel}
        onCancel={() => setShowForceCancel(false)}
        isPending={forceCancel.isPending}
      />
      <ConfirmDialog
        open={showFullSyncConfirm}
        title="Full Sync"
        message="This will re-fetch and re-process ALL products from Octopus, ignoring the last sync timestamp. This takes longer than a delta sync but ensures all products are up to date. Continue?"
        confirmLabel="Start Full Sync"
        onConfirm={() => {
          setShowFullSyncConfirm(false);
          triggerFullSync.mutate();
        }}
        onCancel={() => setShowFullSyncConfirm(false)}
        isPending={triggerFullSync.isPending}
      />

      {/* Overview cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Products Synced" value={stats.productsMapped} icon={Package} color="text-primary" />
        <StatCard
          label="Locations Mapped"
          value={`${stats.locations.mapped} / ${stats.locations.total}`}
          icon={MapPin}
          color="text-blue-600"
        />
        <StatCard
          label="Success Rate (24h)"
          value={stats.last24h.totalRuns > 0 ? `${successRate24h}%` : 'N/A'}
          icon={successRate24h >= 90 ? CheckCircle2 : AlertTriangle}
          color={
            stats.last24h.totalRuns === 0 ? 'text-gray-400' : successRate24h >= 90 ? 'text-green-600' : 'text-amber-600'
          }
        />
        <StatCard
          label="Last Sync"
          value={
            lastSyncAge !== null
              ? lastSyncAge < 60
                ? `${lastSyncAge}m ago`
                : `${Math.round(lastSyncAge / 60)}h ago`
              : 'Never'
          }
          icon={Clock}
          color={lastSyncAge !== null && lastSyncAge <= 10 ? 'text-green-600' : 'text-muted-foreground'}
          subtext={stats.lastSync ? `Status: ${stats.lastSync.status}` : undefined}
        />
      </div>

      {/* Sync frequency + trigger */}
      <div className="rounded-xl border border-border bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Sync Control</h3>
            <p className="text-sm text-muted-foreground">
              {stats.integration.syncMode === 'scheduled' && stats.integration.syncScheduleTimes ? (
                <>
                  Auto-sync at{' '}
                  <span className="font-medium text-foreground">
                    {(() => {
                      try {
                        const times: string[] = JSON.parse(stats.integration.syncScheduleTimes);
                        return times
                          .map((t) => {
                            const [h, m] = t.split(':').map(Number);
                            const suffix = h < 12 ? 'AM' : 'PM';
                            const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                            return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
                          })
                          .join(', ');
                      } catch {
                        return 'configured times';
                      }
                    })()}
                  </span>
                  {' PT'}
                </>
              ) : stats.integration.syncMode === 'daily' && stats.integration.syncDailyTimePst ? (
                <>
                  Auto-sync daily at{' '}
                  <span className="font-medium text-foreground">
                    {(() => {
                      const [h, m] = stats.integration.syncDailyTimePst!.split(':').map(Number);
                      const suffix = h < 12 ? 'AM' : 'PM';
                      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                      return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
                    })()}
                  </span>
                  {' PT'}
                </>
              ) : (
                <>
                  Auto-sync every{' '}
                  <span className="font-medium text-foreground">
                    {Math.round(stats.integration.syncIntervalSec / 60)} minutes
                  </span>
                </>
              )}
              {' · '}
              Connected to <span className="font-medium text-foreground">{stats.integration.apiUrl}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isSyncRunning && (
              <>
                <button
                  onClick={handleStopClick}
                  disabled={cancelSync.isPending}
                  className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  {cancelSync.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <StopCircle className="h-4 w-4" />
                  )}
                  Stop Sync
                </button>
                <button
                  onClick={() => setShowForceCancel(true)}
                  disabled={forceCancel.isPending}
                  className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-red-100 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-200 disabled:opacity-50"
                >
                  {forceCancel.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                  Force Cancel
                </button>
              </>
            )}
            <button
              onClick={handleSyncNowClick}
              disabled={triggerSync.isPending || triggerFullSync.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {triggerSync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync Now
            </button>
            <button
              onClick={() => setShowFullSyncConfirm(true)}
              disabled={triggerFullSync.isPending || triggerSync.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
            >
              {triggerFullSync.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Full Sync
            </button>
          </div>
        </div>
        {(() => {
          const syncResult = triggerSync.data?.data ?? triggerFullSync.data?.data;
          if (!syncResult) return null;
          return (
            <div className="mt-3 rounded-md bg-green-50 p-3 text-sm text-green-800">
              Sync completed: {syncResult.created} created, {syncResult.updated} updated
              {(syncResult.errors?.length ?? 0) > 0 && (
                <span className="text-amber-700"> ({syncResult.errors.length} errors)</span>
              )}
            </div>
          );
        })()}
        {cancelSync.isSuccess && (
          <div className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">Sync has been stopped.</div>
        )}
        {forceCancel.isSuccess && (
          <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800">Sync has been force-cancelled.</div>
        )}
      </div>

      {/* Live sync progress */}
      {isSyncRunning &&
        (() => {
          const runningSync = stats.recentRuns.find((r) => r.status === 'running');
          if (!runningSync) return null;
          return <SyncProgressCard run={runningSync} />;
        })()}

      {/* Period comparison */}
      <div className="grid gap-4 sm:grid-cols-2">
        <PeriodCard title="Last 24 Hours" stats={stats.last24h} />
        <PeriodCard title="Last 7 Days" stats={stats.last7d} />
      </div>

      {/* Last sync details */}
      {stats.lastSync && (
        <div className="rounded-xl border border-border bg-white p-6">
          <h3 className="mb-3 text-lg font-semibold">Last Sync Details</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <SyncRunStatusBadge status={stats.lastSync.status} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Started</p>
              <p className="text-sm font-medium">{new Date(stats.lastSync.startedAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Products Created</p>
              <p className="text-sm font-medium text-green-600">{stats.lastSync.productsCreated}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Products Updated</p>
              <p className="text-sm font-medium text-blue-600">{stats.lastSync.productsUpdated}</p>
            </div>
          </div>
          {stats.lastSync.errorMessage && (
            <div className="mt-3 rounded-md bg-red-50 p-3 text-xs text-red-700">
              <strong>Error:</strong> {stats.lastSync.errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Live Sync Progress Card ──
function SyncProgressCard({ run }: { run: SyncRun }) {
  const phase = run.currentPhase || 'starting';
  const total = run.totalProducts || 0;
  const processed = run.processedProducts || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;

  const phaseLabel: Record<string, string> = {
    starting: 'Initializing sync...',
    fetching: 'Fetching product list from Octopus...',
    enriching: 'Loading product details from Octopus...',
    processing: 'Syncing products to dashboard...',
  };

  // Track elapsed time with a ticking state so we don't call Date.now() in render
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Math.round((now - new Date(run.startedAt).getTime()) / 1000);
  const elapsedStr =
    elapsed < 60
      ? `${elapsed}s`
      : elapsed < 3600
        ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`
        : `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`;

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <h3 className="text-sm font-semibold text-blue-800">Sync In Progress</h3>
        </div>
        <span className="text-xs text-blue-600">{elapsedStr} elapsed</span>
      </div>

      <p className="text-sm text-blue-700 mb-2">{phaseLabel[phase] ?? phase}</p>

      {/* Progress bar */}
      {total > 0 && (
        <>
          <div className="h-2.5 w-full rounded-full bg-blue-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-blue-500 transition-all duration-500 ease-out"
              style={{ width: `${Math.max(pct, 1)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs text-blue-600">
              {processed.toLocaleString()} / {total.toLocaleString()} products
            </span>
            <span className="text-xs font-medium text-blue-700">{pct}%</span>
          </div>
        </>
      )}

      {/* Live counters */}
      {(run.productsCreated > 0 || run.productsUpdated > 0 || run.productsSkipped > 0) && (
        <div className="flex gap-4 mt-3 pt-3 border-t border-blue-200/60">
          <div className="text-xs">
            <span className="text-blue-500">Created:</span>{' '}
            <span className="font-medium text-green-700">{run.productsCreated}</span>
          </div>
          <div className="text-xs">
            <span className="text-blue-500">Updated:</span>{' '}
            <span className="font-medium text-blue-700">{run.productsUpdated}</span>
          </div>
          <div className="text-xs">
            <span className="text-blue-500">Skipped:</span>{' '}
            <span className="font-medium text-amber-700">{run.productsSkipped}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sync Run Status Badge (reusable for history table and monitoring) ──
function SyncRunStatusBadge({ status }: { status: string }) {
  const config = {
    completed: { bg: 'bg-green-100 text-green-700', icon: CheckCircle2 },
    running: { bg: 'bg-blue-100 text-blue-700', icon: Activity },
    cancelled: { bg: 'bg-amber-100 text-amber-700', icon: Ban },
    failed: { bg: 'bg-red-100 text-red-700', icon: XCircle },
  }[status] ?? { bg: 'bg-gray-100 text-gray-600', icon: Activity };

  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${config.bg}`}>
      <Icon className="h-2.5 w-2.5" />
      {status}
    </span>
  );
}

// ── Period Stats Card ──
function PeriodCard({ title, stats }: { title: string; stats: SyncStats['last24h'] }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="font-semibold">{title}</h4>
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Total Runs</p>
          <p className="text-lg font-bold">{stats.totalRuns}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Success Rate</p>
          <p
            className={`text-lg font-bold ${stats.totalRuns > 0 ? (stats.successRate >= 90 ? 'text-green-600' : 'text-amber-600') : 'text-gray-400'}`}
          >
            {stats.totalRuns > 0 ? `${stats.successRate}%` : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Successful</p>
          <p className="text-sm font-medium text-green-600">{stats.successRuns}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Failed</p>
          <p className={`text-sm font-medium ${stats.failedRuns > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {stats.failedRuns}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Products Created</p>
          <p className="text-sm font-medium">{stats.totalCreated}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Products Updated</p>
          <p className="text-sm font-medium">{stats.totalUpdated}</p>
        </div>
      </div>
    </div>
  );
}

// ── Setup Tab ──
function SetupTab({ tenantId }: { tenantId: string }) {
  return (
    <div className="space-y-6">
      <ConnectionConfig tenantId={tenantId} />
      <LocationMapping tenantId={tenantId} />
      <VendorFilter tenantId={tenantId} />
      <UnitMappingConfig tenantId={tenantId} />
    </div>
  );
}

// ── History Tab ──
function HistoryTab({ tenantId, stats }: { tenantId: string; stats: SyncStats | null }) {
  const triggerSync = useTriggerOctopusSync(tenantId);
  const cancelSync = useCancelOctopusSync(tenantId);
  const runs: SyncRun[] = stats?.recentRuns ?? [];
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);

  const isSyncRunning = runs.some((r) => r.status === 'running');

  function handleSyncNowClick() {
    if (isSyncRunning) {
      setShowSyncConfirm(true);
    } else {
      triggerSync.mutate();
    }
  }

  function handleConfirmSync() {
    setShowSyncConfirm(false);
    triggerSync.mutate();
  }

  function handleStopClick() {
    setShowStopConfirm(true);
  }

  function handleConfirmStop() {
    setShowStopConfirm(false);
    cancelSync.mutate();
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog
        open={showSyncConfirm}
        title="Sync Already Running"
        message="A sync is currently in progress. Starting a new sync will cancel the running one. Are you sure you want to proceed?"
        confirmLabel="Start New Sync"
        onConfirm={handleConfirmSync}
        onCancel={() => setShowSyncConfirm(false)}
        isPending={triggerSync.isPending}
      />
      <ConfirmDialog
        open={showStopConfirm}
        title="Stop Running Sync"
        message="Are you sure you want to stop the currently running sync? Products already processed will be kept."
        confirmLabel="Stop Sync"
        confirmVariant="danger"
        onConfirm={handleConfirmStop}
        onCancel={() => setShowStopConfirm(false)}
        isPending={cancelSync.isPending}
      />

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Recent Sync Runs</h3>
        <div className="flex items-center gap-2">
          {isSyncRunning && (
            <button
              onClick={handleStopClick}
              disabled={cancelSync.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {cancelSync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <StopCircle className="h-4 w-4" />}
              Stop Sync
            </button>
          )}
          <button
            onClick={handleSyncNowClick}
            disabled={triggerSync.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {triggerSync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Sync Now
          </button>
        </div>
      </div>

      {runs.length === 0 ? (
        <div className="rounded-xl border border-border bg-white p-8 text-center">
          <Clock className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No sync history yet.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-gray-700">Time</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-right text-[13px] font-semibold text-gray-700">Created</th>
                <th className="px-4 py-3 text-right text-[13px] font-semibold text-gray-700">Updated</th>
                <th className="px-4 py-3 text-right text-[13px] font-semibold text-gray-700">Skipped</th>
                <th className="px-4 py-3 text-right text-[13px] font-semibold text-gray-700">Missing Units</th>
                <th className="px-4 py-3 text-right text-[13px] font-semibold text-gray-700">Missing Conversion</th>
                <th className="px-4 py-3 text-right text-[13px] font-semibold text-gray-700">Unknown Units</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-gray-700">Duration</th>
                <th className="px-4 py-3 text-left text-[13px] font-semibold text-gray-700">Error</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const duration = run.completedAt
                  ? `${((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000).toFixed(1)}s`
                  : null;
                const progressPct =
                  run.status === 'running' && run.totalProducts > 0
                    ? Math.round((run.processedProducts / run.totalProducts) * 100)
                    : null;
                return (
                  <tr key={run.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2.5 text-muted-foreground">{new Date(run.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${run.syncType === 'manual' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}
                      >
                        {run.syncType}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-1">
                        <SyncRunStatusBadge status={run.status} />
                        {run.status === 'running' && progressPct !== null && (
                          <div className="flex items-center gap-1.5">
                            <div className="h-1 w-16 rounded-full bg-blue-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-blue-500 transition-all duration-500"
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-[9px] text-blue-600">{progressPct}%</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-green-600">{run.productsCreated}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-blue-600">{run.productsUpdated}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-yellow-600">{run.productsSkipped}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-orange-600">
                      {run.skippedMissingUnits || 0}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-orange-600">
                      {run.skippedMissingConversion || 0}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-red-600">{run.skippedUnknownUnits || 0}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{duration ?? 'Running...'}</td>
                    <td className="px-4 py-2.5 max-w-64 text-xs text-red-600">
                      {run.errorMessage ? (
                        <details className="cursor-pointer">
                          <summary className="truncate max-w-48">
                            {run.errorMessage.split('\n').length} error(s)
                          </summary>
                          <div className="mt-1 max-h-40 overflow-y-auto rounded bg-red-50 p-2 text-[11px] whitespace-pre-wrap">
                            {run.errorMessage}
                          </div>
                        </details>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Connection Config ──
function ConnectionConfig({ tenantId }: { tenantId: string }) {
  const { data: configRes } = useOctopusIntegration(tenantId);
  const saveConfig = useSaveOctopusIntegration();
  const testConnection = useTestOctopusConnection();

  const config = configRes?.data;

  const [apiUrl, setApiUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [syncMode, setSyncMode] = useState<'interval' | 'daily' | 'scheduled'>('interval');
  const [syncInterval, setSyncInterval] = useState(300);
  const [syncDailyTimePst, setSyncDailyTimePst] = useState('02:00');
  const [scheduleTimes, setScheduleTimes] = useState<string[]>(['12:00']);
  const [initialized, setInitialized] = useState(false);

  if (config && !initialized) {
    setApiUrl(config.apiUrl || '');
    setApiToken(config.apiToken || '');
    setIsActive(config.isActive || false);
    const mode = config.syncMode === 'daily' ? 'daily' : config.syncMode === 'scheduled' ? 'scheduled' : 'interval';
    setSyncMode(mode);
    setSyncInterval(config.syncIntervalSec || 300);
    setSyncDailyTimePst(config.syncDailyTimePst || '02:00');
    if (config.syncScheduleTimes) {
      try {
        const parsed = JSON.parse(config.syncScheduleTimes);
        if (Array.isArray(parsed) && parsed.length > 0) setScheduleTimes(parsed);
      } catch {
        /* malformed JSON — keep default */
      }
    }
    setInitialized(true);
  }

  function handleSave() {
    saveConfig.mutate({
      tenantId,
      apiUrl,
      apiToken,
      isActive,
      syncMode,
      syncIntervalSec: syncInterval,
      syncDailyTimePst: syncMode === 'daily' ? syncDailyTimePst : undefined,
      syncScheduleTimes: syncMode === 'scheduled' ? scheduleTimes : undefined,
    });
  }

  function handleTest() {
    testConnection.mutate({ apiUrl, apiToken });
  }

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
          <Settings2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Connection Configuration</h3>
          <p className="text-sm text-muted-foreground">Configure the Octopus POS API connection</p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">API URL</label>
            <input
              type="text"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="https://example.novainstore.com"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">API Token</label>
            <input
              type="password"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Enter API token"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="octopus-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <label htmlFor="octopus-active" className="text-sm font-medium">
            Enable automatic sync
          </label>
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-4">
          <div className="mb-3 text-sm font-medium">Sync schedule</div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="sync-mode"
                value="interval"
                checked={syncMode === 'interval'}
                onChange={() => setSyncMode('interval')}
                className="h-4 w-4"
              />
              Interval
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="sync-mode"
                value="daily"
                checked={syncMode === 'daily'}
                onChange={() => setSyncMode('daily')}
                className="h-4 w-4"
              />
              Once a day
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="sync-mode"
                value="scheduled"
                checked={syncMode === 'scheduled'}
                onChange={() => setSyncMode('scheduled')}
                className="h-4 w-4"
              />
              Scheduled times
            </label>
          </div>
          {syncMode === 'interval' && (
            <div className="mt-3 flex items-center gap-2">
              <label htmlFor="sync-interval" className="text-sm">
                Sync every
              </label>
              <select
                id="sync-interval"
                className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                value={syncInterval}
                onChange={(e) => setSyncInterval(Number(e.target.value))}
              >
                <option value={60}>1 minute</option>
                <option value={180}>3 minutes</option>
                <option value={300}>5 minutes</option>
                <option value={600}>10 minutes</option>
                <option value={900}>15 minutes</option>
                <option value={1800}>30 minutes</option>
                <option value={3600}>1 hour</option>
              </select>
            </div>
          )}
          {syncMode === 'daily' && (
            <div className="mt-3 flex items-center gap-2">
              <label htmlFor="sync-daily-time" className="text-sm">
                Run daily at
              </label>
              <input
                id="sync-daily-time"
                type="time"
                value={syncDailyTimePst}
                onChange={(e) => setSyncDailyTimePst(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1 text-sm"
              />
              <span className="text-xs text-muted-foreground">Pacific time (DST-aware)</span>
            </div>
          )}
          {syncMode === 'scheduled' && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                Add one or more daily sync times (Pacific time, DST-aware)
              </p>
              {scheduleTimes.map((time, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => {
                      const updated = [...scheduleTimes];
                      updated[idx] = e.target.value;
                      setScheduleTimes(updated);
                    }}
                    className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-muted-foreground">
                    {(() => {
                      const [h] = time.split(':').map(Number);
                      return h < 12 ? 'AM' : 'PM';
                    })()}
                  </span>
                  {scheduleTimes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setScheduleTimes(scheduleTimes.filter((_, i) => i !== idx))}
                      className="text-red-500 hover:text-red-700"
                      title="Remove this time"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setScheduleTimes([...scheduleTimes, '12:00'])}
                className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                <Plus className="h-3 w-3" /> Add time
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleTest}
            disabled={testConnection.isPending || !apiUrl || !apiToken}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            {testConnection.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
            Test Connection
          </button>
          <button
            onClick={handleSave}
            disabled={saveConfig.isPending || !apiUrl || !apiToken}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saveConfig.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Configuration
          </button>
        </div>
        {testConnection.data?.data && (
          <div
            className={`rounded-md p-3 text-sm ${
              testConnection.data.data.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {testConnection.data.data.success ? (
              <span className="flex items-center gap-2">
                <Wifi className="h-4 w-4" /> {testConnection.data.data.message}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <WifiOff className="h-4 w-4" /> {testConnection.data.data.message}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Location Mapping ──
function LocationMapping({ tenantId }: { tenantId: string }) {
  const { data: mappingsRes } = useOctopusLocationMappings(tenantId);
  const { data: tenantRes } = useAdminTenantDetail(tenantId);
  const discover = useDiscoverOctopusLocations(tenantId);
  const saveMappings = useSaveOctopusLocationMappings(tenantId);
  const deleteMapping = useDeleteOctopusLocationMapping(tenantId);

  const mappings = mappingsRes?.data ?? [];
  const locations: any[] = tenantRes?.data?.locations ?? [];

  const serverMappings = useMemo(() => {
    const initial: Record<number, string | null> = {};
    for (const m of mappings) {
      initial[m.octopusLocationId] = m.dashboardLocationId;
    }
    return initial;
  }, [mappings]);

  const [localOverrides, setLocalOverrides] = useState<Record<number, string | null>>({});
  const localMappings = { ...serverMappings, ...localOverrides };

  function handleDiscover() {
    discover.mutate();
  }

  function handleSave() {
    const toSave = mappings.map((m) => ({
      octopusLocationId: m.octopusLocationId,
      dashboardLocationId: localMappings[m.octopusLocationId] || null,
      octopusLocationName: m.octopusLocationName || undefined,
    }));
    saveMappings.mutate(toSave);
  }

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
            <MapPin className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Location Mapping</h3>
            <p className="text-sm text-muted-foreground">Map Octopus locations to dashboard locations</p>
          </div>
        </div>
        <button
          onClick={handleDiscover}
          disabled={discover.isPending}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          {discover.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Discover Locations
        </button>
      </div>

      {discover.data?.data && (
        <p className="mb-3 text-sm text-muted-foreground">
          Discovered {discover.data.data.length} location(s) in Octopus POS
        </p>
      )}

      {mappings.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No locations discovered yet. Click "Discover Locations" to scan Octopus for available locations.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium">Octopus Location</th>
                  <th className="pb-2 font-medium">Dashboard Location</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m) => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="py-2">
                      <span className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        {m.octopusLocationName || `Location ${m.octopusLocationId}`} (ID: {m.octopusLocationId})
                      </span>
                    </td>
                    <td className="py-2">
                      <select
                        className="rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                        value={localMappings[m.octopusLocationId] || ''}
                        onChange={(e) =>
                          setLocalOverrides((prev) => ({
                            ...prev,
                            [m.octopusLocationId]: e.target.value || null,
                          }))
                        }
                      >
                        <option value="">-- Not mapped --</option>
                        {locations.map((loc: any) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => deleteMapping.mutate(m.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove mapping"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <button
              onClick={handleSave}
              disabled={saveMappings.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saveMappings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Mappings
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Vendor Filter ──
// Per-tenant Octopus vendor allow-list. When any vendor is checked, the sync
// engine only upserts products whose Octopus vendors[] includes one of the
// checked vendors. Nothing checked ⇒ no filter (legacy: sync everything).
function VendorFilter({ tenantId }: { tenantId: string }) {
  const { data: vendorsRes, isLoading } = useOctopusVendorSelections(tenantId);
  const discover = useDiscoverOctopusVendors(tenantId);
  const saveSelections = useSaveOctopusVendorSelections(tenantId);

  const vendors = vendorsRes?.data ?? [];

  const serverActive = useMemo(() => {
    const s = new Set<number>();
    for (const v of vendors) if (v.isActive) s.add(v.octopusVendorId);
    return s;
  }, [vendors]);

  const [localActive, setLocalActive] = useState<Set<number> | null>(null);
  const active = localActive ?? serverActive;

  const [search, setSearch] = useState('');
  const visibleVendors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter(
      (v) => (v.octopusVendorName || '').toLowerCase().includes(q) || String(v.octopusVendorId).includes(q),
    );
  }, [vendors, search]);

  const dirty = localActive !== null;

  function toggle(vendorId: number) {
    const next = new Set(active);
    if (next.has(vendorId)) next.delete(vendorId);
    else next.add(vendorId);
    setLocalActive(next);
  }

  function selectAllVisible() {
    const next = new Set(active);
    for (const v of visibleVendors) next.add(v.octopusVendorId);
    setLocalActive(next);
  }

  function clearAllVisible() {
    const next = new Set(active);
    for (const v of visibleVendors) next.delete(v.octopusVendorId);
    setLocalActive(next);
  }

  function handleSave() {
    saveSelections.mutate(Array.from(active), {
      onSuccess: () => setLocalActive(null),
    });
  }

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
            <Tag className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Vendor Filter</h3>
            <p className="text-sm text-muted-foreground">
              Restrict sync to products that belong to selected Octopus vendors. Leave all unchecked to sync every
              product.
            </p>
          </div>
        </div>
        <button
          onClick={() => discover.mutate()}
          disabled={discover.isPending}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          {discover.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Discover Vendors
        </button>
      </div>

      {discover.data?.data && (
        <p className="mb-3 text-sm text-muted-foreground">
          Discovered {discover.data.data.discovered} vendor(s) in Octopus POS
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading vendors…</p>
      ) : vendors.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No vendors discovered yet. Click "Discover Vendors" to pull the list from Octopus.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vendors by name or ID"
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 pl-8 text-sm outline-none focus:border-primary"
              />
            </div>
            <button
              type="button"
              onClick={selectAllVisible}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Select all{search ? ' (filtered)' : ''}
            </button>
            <button
              type="button"
              onClick={clearAllVisible}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Clear{search ? ' (filtered)' : ''}
            </button>
            <span className="text-xs text-muted-foreground">
              {active.size} of {vendors.length} selected
            </span>
          </div>

          <div className="max-h-96 overflow-y-auto rounded-md border border-border">
            <ul className="divide-y divide-border">
              {visibleVendors.map((v) => (
                <li key={v.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30">
                  <input
                    type="checkbox"
                    id={`vendor-${v.octopusVendorId}`}
                    checked={active.has(v.octopusVendorId)}
                    onChange={() => toggle(v.octopusVendorId)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor={`vendor-${v.octopusVendorId}`} className="flex-1 cursor-pointer text-sm">
                    <span className="font-medium">{v.octopusVendorName || `Vendor ${v.octopusVendorId}`}</span>
                    <span className="ml-2 text-xs text-muted-foreground">ID: {v.octopusVendorId}</span>
                  </label>
                </li>
              ))}
              {visibleVendors.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted-foreground">No vendors match "{search}"</li>
              )}
            </ul>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={!dirty || saveSelections.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saveSelections.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Selections
            </button>
            {dirty && (
              <button
                type="button"
                onClick={() => setLocalActive(null)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            )}
            {active.size === 0 && !dirty && (
              <span className="text-xs text-muted-foreground">
                No vendor filter active — sync will pull every product (legacy behavior).
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Unit Mapping Config ──
const DASHBOARD_UNITS = [
  'each',
  'case',
  'lb',
  'oz',
  'kg',
  'g',
  'pack',
  'box',
  'dozen',
  'gallon',
  'liter',
  'pint',
  'quart',
];

function UnitMappingConfig({ tenantId }: { tenantId: string }) {
  const { data: mappingsRes, isLoading } = useOctopusUnitMappings(tenantId);
  const saveMappings = useSaveOctopusUnitMappings(tenantId);
  const deleteMapping = useDeleteOctopusUnitMapping(tenantId);

  const mappings: OctopusUnitMapping[] = mappingsRes?.data ?? [];

  const [newUnitId, setNewUnitId] = useState('');
  const [newUnitName, setNewUnitName] = useState('each');
  const [newConversionQty, setNewConversionQty] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editUnitName, setEditUnitName] = useState('');
  const [editConversionQty, setEditConversionQty] = useState('');

  function handleAdd() {
    const octId = parseInt(newUnitId, 10);
    if (isNaN(octId) || octId < 0 || !newUnitName) return;
    const convQty = newConversionQty ? parseInt(newConversionQty, 10) : null;
    saveMappings.mutate([{ octopusUnitId: octId, unitName: newUnitName, conversionQty: convQty }], {
      onSuccess: () => {
        setNewUnitId('');
        setNewUnitName('each');
        setNewConversionQty('');
      },
    });
  }

  function handleEdit(mapping: OctopusUnitMapping) {
    setEditingId(mapping.id);
    setEditUnitName(mapping.unitName);
    setEditConversionQty(mapping.conversionQty ? String(mapping.conversionQty) : '');
  }

  function handleSaveEdit(mapping: OctopusUnitMapping) {
    if (!editUnitName) return;
    const convQty = editConversionQty ? parseInt(editConversionQty, 10) : null;
    saveMappings.mutate([{ octopusUnitId: mapping.octopusUnitId, unitName: editUnitName, conversionQty: convQty }], {
      onSuccess: () => setEditingId(null),
    });
  }

  function handleDelete(id: string) {
    deleteMapping.mutate(id);
  }

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
          <Settings2 className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Unit Mapping</h3>
          <p className="text-sm text-muted-foreground">
            Map Octopus numeric unit IDs to dashboard unit names. Set a conversion qty when the unit represents multiple
            base units (e.g. &ldquo;PACK OF 2&rdquo; = each with conversion qty 2).
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading unit mappings...</p>
      ) : (
        <>
          {mappings.length > 0 && (
            <div className="mb-4 overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Octopus Unit ID</th>
                    <th className="px-4 py-2 text-left font-medium">Dashboard Unit Name</th>
                    <th className="px-4 py-2 text-left font-medium">Conversion Qty</th>
                    <th className="px-4 py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mappings.map((m) => (
                    <tr key={m.id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono">{m.octopusUnitId}</td>
                      <td className="px-4 py-2">
                        {editingId === m.id ? (
                          <select
                            value={editUnitName}
                            onChange={(e) => setEditUnitName(e.target.value)}
                            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                          >
                            {DASHBOARD_UNITS.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {m.unitName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {editingId === m.id ? (
                          <input
                            type="number"
                            min="1"
                            value={editConversionQty}
                            onChange={(e) => setEditConversionQty(e.target.value)}
                            placeholder="e.g. 2"
                            className="w-20 rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                          />
                        ) : m.conversionQty ? (
                          <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                            {m.conversionQty} each
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {editingId === m.id ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSaveEdit(m)}
                              disabled={saveMappings.isPending}
                              className="text-xs text-primary hover:underline disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="text-xs text-muted-foreground hover:underline"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => handleEdit(m)} className="text-xs text-primary hover:underline">
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(m.id)}
                              disabled={deleteMapping.isPending}
                              className="text-xs text-destructive hover:underline disabled:opacity-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-3">
            <input
              type="number"
              min="0"
              value={newUnitId}
              onChange={(e) => setNewUnitId(e.target.value)}
              placeholder="Octopus Unit ID"
              className="w-36 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
            <select
              value={newUnitName}
              onChange={(e) => setNewUnitName(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              {DASHBOARD_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="1"
              value={newConversionQty}
              onChange={(e) => setNewConversionQty(e.target.value)}
              placeholder="Conv. Qty"
              title="How many base units (each) this unit represents. E.g. 2 for PACK OF 2"
              className="w-24 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
            <button
              onClick={handleAdd}
              disabled={!newUnitId || saveMappings.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saveMappings.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Add Mapping
            </button>
          </div>

          {mappings.length === 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              No custom unit mappings configured. The sync engine will use built-in defaults (1=each, 2=lb, 3=oz, etc.).
              Add mappings here to override for this tenant.
            </p>
          )}
        </>
      )}
    </div>
  );
}
